import React from 'react';
import { Eye, AlertTriangle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui';
import type { ValidationResult } from '../../utils';
import {
  getValidationErrorsByRow,
  getValidationErrorsByCell,
} from '../../utils';

type CellValue = string | number | boolean | Date | null;
type RowData = CellValue[];
type TableData = RowData[];

interface DataPreviewTableProps {
  data: TableData;
  title?: string;
  description?: string;
  _maxRows?: number;
  maxColumns?: number;
  className?: string;
  validation?: ValidationResult | null;
  _totalRows?: number; // Total rows in the full dataset
}

interface ValidationError {
  rowIndex: number;
  column: string;
  columnIndex: number;
  errorType: string;
  message: string;
}

// Helper function to categorize validation errors
const categorizeErrors = (errors: ValidationError[]) => {
  const warnings = errors.filter(
    (error) =>
      error.errorType === 'MISSING_DATA' || error.errorType === 'EMPTY_ROW'
  );
  const criticalErrors = errors.filter(
    (error) =>
      error.errorType === 'INVALID_QUANTITY' ||
      error.errorType === 'INVALID_FORMAT'
  );
  return { warnings, criticalErrors };
};

export const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  data,
  title,
  description,
  _maxRows, // Will be ignored - show all rows
  maxColumns = 8,
  className = '',
  validation,
  _totalRows,
}) => {
  const { t } = useTranslation();

  // Use translations as default values if props are not provided
  const displayTitle = title || t('tools.dataPreview.title');

  const errorsByRow = validation
    ? getValidationErrorsByRow(validation.errors)
    : new Map();
  const errorsByCell = validation
    ? getValidationErrorsByCell(validation.errors)
    : new Map();

  if (data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
            {displayTitle}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            {t('tools.dataPreview.noData')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
            {displayTitle}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            {description ||
              t('tools.dataPreview.rowsShowing', {
                count: data.length,
              })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto max-h-screen">
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-xs sm:text-sm">
                <tbody>
                  {data.map((row, rowIndex) => {
                    const rowErrors = errorsByRow.get(rowIndex);
                    const hasRowErrors = rowErrors && rowErrors.length > 0;
                    const isHeaderRow = rowIndex === 0;

                    return (
                      <tr
                        key={rowIndex}
                        className={`
                      ${isHeaderRow ? 'bg-gray-50 font-medium sticky top-0 z-10' : ''}
                      ${hasRowErrors && !isHeaderRow ? 'bg-red-50' : ''}
                    `}
                      >
                        {row.slice(0, maxColumns).map((cell, cellIndex) => {
                          const cellKey = `${rowIndex}-${cellIndex}`;
                          const cellError = errorsByCell.get(cellKey);
                          const hasCellError = cellError && !isHeaderRow;

                          return (
                            <td
                              key={cellIndex}
                              className={`
                            px-2 sm:px-3 py-1 sm:py-2 border-r border-gray-200 max-w-24 sm:max-w-32 truncate relative
                            ${hasCellError ? 'bg-red-100 border-red-300' : ''}
                          `}
                              title={
                                hasCellError
                                  ? `${String(cell || '')} - ${cellError.message}`
                                  : String(cell || '')
                              }
                            >
                              <div className="flex items-center gap-1">
                                {String(cell || '')}
                                {hasCellError && (
                                  <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                                )}
                              </div>
                            </td>
                          );
                        })}
                        {hasRowErrors && !isHeaderRow && (
                          <td className="px-2 sm:px-3 py-1 sm:py-2 border-l border-red-300 bg-red-100">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1 text-red-700 cursor-pointer">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span className="text-xs">
                                    {rowErrors.length} issue
                                    {rowErrors.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3">
                                <div className="space-y-2">
                                  {(() => {
                                    const { warnings, criticalErrors } =
                                      categorizeErrors(rowErrors);
                                    return (
                                      <>
                                        {warnings.length > 0 && (
                                          <div className="space-y-1">
                                            <div className="flex items-center gap-1 text-yellow-600 font-medium text-xs">
                                              <AlertTriangle className="w-3 h-3" />
                                              <span>
                                                Warnings ({warnings.length})
                                              </span>
                                            </div>
                                            {warnings.map((warning, idx) => (
                                              <div
                                                key={idx}
                                                className="text-xs text-gray-700 pl-4"
                                              >
                                                • {warning.message}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {criticalErrors.length > 0 && (
                                          <div className="space-y-1">
                                            <div className="flex items-center gap-1 text-red-600 font-medium text-xs">
                                              <AlertCircle className="w-3 h-3" />
                                              <span>
                                                Errors ({criticalErrors.length})
                                              </span>
                                            </div>
                                            {criticalErrors.map(
                                              (error, idx) => (
                                                <div
                                                  key={idx}
                                                  className="text-xs text-gray-700 pl-4"
                                                >
                                                  • {error.message}
                                                </div>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
